import type { PlacePrediction } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { loadGoogleMaps } from '@/hotdesk-ui/helpers/google-maps-loader';

export interface CitySearchResult {
    /** Stable key: Google place_id, or `api-<id>` for the fallback. */
    id: string;
    name: string;
    countryName?: string;
}

const GOOGLE_TIMEOUT_MS = 4000;

type CityPrediction = PlacePrediction & {
    terms?: Array<{ value: string }>;
};

/**
 * Google Places, cities only. Ranked by prominence, so "lon" gives London
 * first — our `/city/` table has no population/importance to rank by.
 * Null when the Places library isn't available (blocked, not loaded yet).
 */
function searchGoogleCities(
    query: string
): Promise<CitySearchResult[]> | null {
    const places = typeof window !== 'undefined' ? window.google?.maps?.places : undefined;
    if (!places?.AutocompleteService) return null;
    const service = new places.AutocompleteService();

    return new Promise((resolve, reject) => {
        const timer = window.setTimeout(
            () => reject(new Error('Places autocomplete timed out')),
            GOOGLE_TIMEOUT_MS
        );
        service.getPlacePredictions(
            { input: query, types: ['(cities)'] },
            (preds: CityPrediction[] | null, status: string) => {
                window.clearTimeout(timer);
                if (status === 'ZERO_RESULTS') return resolve([]);
                if (status !== 'OK' || !preds) {
                    return reject(new Error(`Places autocomplete: ${status}`));
                }
                resolve(
                    preds.map((p) => ({
                        id: p.place_id,
                        name: p.structured_formatting.main_text,
                        // Last term is the country ("Cairo, Cairo Governorate, Egypt").
                        countryName:
                            p.terms?.[p.terms.length - 1]?.value ??
                            p.structured_formatting.secondary_text
                    }))
                );
            }
        );
    });
}

/** Our `/api/cities` proxy (ranked by match quality). */
async function searchApiCities(
    query: string,
    limit: number
): Promise<CitySearchResult[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const res = await fetch(`/api/cities?${params.toString()}`, {
        headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to load cities');
    const json = await res.json();
    const results = (json?.results || []) as Array<{
        id?: number;
        name?: string;
        countryName?: string;
    }>;
    return results
        .filter((c) => c?.id != null && c?.name)
        .map((c) => ({
            id: `api-${c.id}`,
            name: c.name as string,
            countryName: c.countryName || undefined
        }));
}

/** Google Places when available, our city API otherwise (or if it fails). */
export async function searchCities(
    query: string,
    limit = 40
): Promise<CitySearchResult[]> {
    // Maps loads on demand; a failed load falls through to the city API.
    await loadGoogleMaps().catch(() => undefined);
    const google = searchGoogleCities(query);
    if (google) {
        try {
            return await google;
        } catch (err) {
            console.warn('City search: falling back to city API', err);
        }
    }
    return searchApiCities(query, limit);
}
